import React, { useMemo } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import styles from "../../appointmentsPage.module.css";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { IDayCancellationRate } from "../../types/noShowPredictor.types";

interface ICancellationsChartProps {
  data: IDayCancellationRate[];
}

/**
 * Bar chart showing cancellation rates by day of week
 */
const CancellationsChart: React.FC<ICancellationsChartProps> = ({ data }) => {
  const primaryColor = "#6B46C1";
  const lightColor = "#8B5CF6";

  // Translate day names based on dayKey
  const translatedData = useMemo(() => {
    return data.map(item => ({
      ...item,
      dayName: t(`APPOINTMENTS_DAY_${item.dayKey}`)
    }));
  }, [data]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className={styles.chartTooltip}>
          <strong>{payload[0].payload.dayName}</strong>
          <div>{payload[0].value}% {t("NOSHOW_STAT_CANCELLATION_RATE").toLowerCase()}</div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={styles.chartContainer}>
      <h3 className={styles.chartTitle}>📈 {t("NOSHOW_CHART_TITLE")}</h3>
      <div className={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={translatedData} margin={{ top: 20, right: 20, left: 20, bottom: 5 }}>
            <XAxis 
              dataKey="dayName" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 12, fontWeight: 600 }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 12 }}
              tickFormatter={(value) => `${value}%`}
              domain={[0, 'auto']}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(107, 70, 193, 0.1)' }} />
            <Bar 
              dataKey="rate" 
              radius={[8, 8, 0, 0]}
              maxBarSize={60}
            >
              {translatedData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={`url(#barGradient)`}
                />
              ))}
            </Bar>
            <defs>
              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={lightColor} />
                <stop offset="100%" stopColor={primaryColor} />
              </linearGradient>
            </defs>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CancellationsChart;
